/**
 * event-buffer.js — module-level singleton that holds reading events between
 * the bimodal reader and the backend.
 *
 * Spec: docs/features/assessment-data-layer.md
 *
 * Responsibilities:
 *   - Queue events in memory, persist to localStorage so reloads don't lose data.
 *   - Flush periodically to /api/events/batch with exponential backoff on failure.
 *   - Flush on pagehide via keepalive fetch (NOT sendBeacon — sendBeacon can't
 *     carry the Authorization header, and our ingest endpoint requires JWT auth).
 *   - Cap buffer at BUFFER_MAX_EVENTS, dropping oldest non-essential events first.
 *   - Captive-portal defense: a flush succeeds only if the response body parses
 *     to JSON with `accepted` as a number. School wifi captive portals return
 *     200 OK with HTML; without this check we'd silently lose every event the
 *     first time a Chromebook hits a portal mid-class.
 *   - Idempotency: every event has a UUID; server dedups on client_event_id.
 *     Retrying after a half-failed flush is safe.
 *   - Cross-attribution defense: events are tagged with access_code at
 *     queue time. If the user logs out and back in as a different student,
 *     the server rejects mismatched events (drops, doesn't 500).
 */

import { apiUrl } from '../config/api';
import {
  BUFFER_MAX_EVENTS,
  BUFFER_STORAGE_KEY,
  FLUSH_BATCH_SIZE,
  FLUSH_INTERVAL_MS,
  KEEPALIVE_CHUNK_SIZE,
  BACKOFF_INITIAL_MS,
  BACKOFF_MAX_MS,
} from '../config/events';

const ESSENTIAL_EVENT_TYPES = new Set(['session_start', 'session_end', 'book_complete']);

class EventBuffer {
  constructor() {
    /** @type {Array<{ event: object, accessCode: string }>} */
    this.queue = [];
    this.token = null;
    this.sessionId = null;
    this.flushTimer = null;
    this.backoffMs = BACKOFF_INITIAL_MS;
    this.flushing = false;
    // suspended = waiting for re-auth after a 401. Buffer survives, no flushes.
    this.suspended = false;

    if (typeof window !== 'undefined') {
      this.loadFromStorage();
      window.addEventListener('pagehide', () => this.flushOnUnload());
      window.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden' && this.queue.length > 0) {
          this.flush();
        }
      });
    }
  }

  /**
   * Bind the auth token + current session_id. Called from useReadingEvents.
   * Schedules a flush if the queue is non-empty (re-auth case).
   */
  setSession({ token, sessionId }) {
    this.token = token;
    this.sessionId = sessionId;
    this.suspended = false;
    if (this.queue.length > 0) {
      this.scheduleFlush(0);
    }
  }

  clearSession() {
    this.token = null;
    this.sessionId = null;
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }

  enqueue(event, accessCode) {
    if (!accessCode) return; // Defensive — never queue events without identity.

    if (this.queue.length >= BUFFER_MAX_EVENTS) {
      // Drop oldest non-essential event first; only fall back to oldest-period
      // if every queued event is essential (rare).
      const idx = this.queue.findIndex(({ event: e }) => !ESSENTIAL_EVENT_TYPES.has(e.event_type));
      if (idx >= 0) {
        this.queue.splice(idx, 1);
      } else {
        this.queue.shift();
      }
    }

    this.queue.push({ event, accessCode });
    this.persist();
    this.scheduleFlush();
  }

  scheduleFlush(delayMs = FLUSH_INTERVAL_MS) {
    if (this.flushTimer) return;
    if (this.suspended) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flush();
    }, delayMs);
  }

  async flush() {
    if (this.suspended || this.flushing) return;
    if (this.queue.length === 0) return;
    if (!this.token || !this.sessionId) return;

    this.flushing = true;
    const batch = this.queue.slice(0, FLUSH_BATCH_SIZE);
    const batchIds = new Set(batch.map(({ event }) => event.client_event_id));

    try {
      const res = await fetch(apiUrl('/api/events/batch'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`,
        },
        body: JSON.stringify({
          session_id: this.sessionId,
          events: batch.map(({ event, accessCode }) => ({ ...event, access_code: accessCode })),
        }),
      });

      if (res.status === 401) {
        // Auth expired. Suspend flushes; buffer survives until next setSession.
        this.suspended = true;
        return;
      }

      if (!res.ok) {
        throw new Error(`flush failed: HTTP ${res.status}`);
      }

      let body;
      try {
        body = await res.json();
      } catch {
        // Not JSON — likely a captive-portal HTML response. Treat as failure.
        throw new Error('flush response not JSON (captive portal?)');
      }

      // Captive-portal defense: server must respond with `accepted` as a number.
      if (typeof body.accepted !== 'number') {
        throw new Error('flush response malformed (no numeric accepted)');
      }

      // Server is idempotent — accepted_ids and rejected ids cover everything
      // that round-tripped successfully. Anything in batchIds not reflected
      // there should remain queued for retry.
      const settled = new Set([
        ...(body.accepted_ids ?? []),
        ...((body.rejected ?? []).map((r) => r.client_event_id)),
      ]);

      if (settled.size === 0) {
        // Nothing was settled (shouldn't happen on a 200 OK). Conservative:
        // assume the whole batch was accepted to avoid an infinite retry loop.
        for (const id of batchIds) settled.add(id);
      }

      this.queue = this.queue.filter(({ event }) => !settled.has(event.client_event_id));
      this.persist();

      // Reset backoff on successful flush.
      this.backoffMs = BACKOFF_INITIAL_MS;

      // More events to flush? Schedule the next one immediately.
      if (this.queue.length > 0) {
        this.scheduleFlush(1000);
      }
    } catch (err) {
      // Exponential backoff on transport failures (offline, server 5xx, captive portal).
      this.backoffMs = Math.min(this.backoffMs * 2, BACKOFF_MAX_MS);
      this.scheduleFlush(this.backoffMs);
    } finally {
      this.flushing = false;
    }
  }

  /**
   * Best-effort flush during pagehide. Fires fetch with keepalive: true so
   * the request continues after the page unloads. Chunked because keepalive
   * has a 64KB total-body cap; 50 events per chunk leaves headroom even for
   * verbose payloads.
   */
  flushOnUnload() {
    if (this.queue.length === 0 || !this.token || !this.sessionId) return;

    const events = this.queue.map(({ event, accessCode }) => ({ ...event, access_code: accessCode }));
    for (let i = 0; i < events.length; i += KEEPALIVE_CHUNK_SIZE) {
      const chunk = events.slice(i, i + KEEPALIVE_CHUNK_SIZE);
      try {
        // No await — pagehide is synchronous; keepalive ensures the request
        // continues after the page is gone.
        fetch(apiUrl('/api/events/batch'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.token}`,
          },
          body: JSON.stringify({ session_id: this.sessionId, events: chunk }),
          keepalive: true,
        });
      } catch {
        // Best effort. Page is unloading; nothing more we can do.
      }
    }

    // Clear the queue locally — server idempotency makes any duplicate
    // arrivals harmless if the user re-opens the tab and we re-flush.
    this.queue = [];
    this.persist();
  }

  persist() {
    try {
      localStorage.setItem(BUFFER_STORAGE_KEY, JSON.stringify(this.queue));
    } catch {
      // Storage full / disabled / private mode — ignore. The in-memory queue
      // still works for the rest of the session.
    }
  }

  loadFromStorage() {
    try {
      const raw = localStorage.getItem(BUFFER_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        this.queue = parsed.filter((entry) => entry && entry.event && entry.accessCode);
      }
    } catch {
      // Corrupt or stale. Drop it.
      try { localStorage.removeItem(BUFFER_STORAGE_KEY); } catch { /* ignore */ }
    }
  }
}

export const eventBuffer = new EventBuffer();
